import { useContext } from 'react';

import { L10nContext } from 'context/L10nContext';

import { ColumnLayoutSection } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSection';
import { ColumnLayoutSectionMode } from 'presentation/ui/layouts/column-layout/column-layout-section/ColumnLayoutSectionMode';
import { ColumnLayout } from 'presentation/ui/layouts/column-layout/ColumnLayout';
import { FormLayoutHeader } from 'presentation/ui/layouts/form-layout/form-layout-header/FormLayoutHeader';
import { FormLayoutSection } from 'presentation/ui/layouts/form-layout/form-layout-section/FormLayoutSection';
import { FormLayout } from 'presentation/ui/layouts/form-layout/FormLayout';
import { FormLayoutModifier } from 'presentation/ui/layouts/form-layout/FormLayoutModifier';
import { FormLayoutSectionAlign } from 'presentation/ui/layouts/form-layout/FormLayoutSectionAlign';
import { ButtonPrimary } from 'presentation/ui/partials/button/button-primary/ButtonPrimary';
import { ButtonSecondary } from 'presentation/ui/partials/button/button-secondary/ButtonSecondary';
import { IconIdentifier } from 'presentation/ui/partials/icon/IconIdentifier';
import { InputText, InputTextStatus } from 'presentation/ui/partials/input/input-text/InputText';
import { Label } from 'presentation/ui/partials/input/label/Label';
import { CreatableEluateViewModel } from 'services/nuclide/domain/model/EluateModel';
import { Permission } from 'services/core/lib/auth/AuthService';
import { AuthContext } from 'services/core/context/AuthContext';
import { getEluateMeasurementUnitDefinition } from 'services/nuclide/domain/model/NuclideMeasurementUnitDefinition';

export interface EluateCreateFormStep2Props {
	/**
	 * Eluate
	 */
	eluate: CreatableEluateViewModel;
	/**
	 * Button back action
	 */
	onClickPrevious: () => void;
	/**
	 * Button confirm
	 */
	onClickConfirm?: () => void;
}

/**
 * The device create modal step 2 component
 */
export const EluateCreateFormStep2 = (props: EluateCreateFormStep2Props): JSX.Element => {
	const { eluate, onClickPrevious, onClickConfirm } = props;

	// Consume the context
	const l10nContext = useContext(L10nContext);
	const authContext = useContext(AuthContext);

	const renderExtractionDatetimeInput = (): JSX.Element | null => {
		if (authContext.hasPermission(Permission.GENERATOR_ELUAT_SUPPLEMENT)) {
			return (
				<FormLayoutSection>
					<Label label={`${l10nContext.translate('eluates.extractionDateTime', 'Extraktionszeitpunkt')}*`}>
						<InputText
							type='text'
							value={extractionDatetimeFormatted}
							status={InputTextStatus.SUMMARY}
							disabled
						/>
					</Label>
				</FormLayoutSection>
			);
		}
		return null;
	};

	const extractionDatetimeFormatted = l10nContext.formatDateTime(new Date(eluate?.ExtractionDatetime));

	return (
		<FormLayout
			align={FormLayoutSectionAlign.ALIGN_HORIZONTAL}
			modifier={FormLayoutModifier.BLUE_BACKGROUND}
			onSubmit={onClickConfirm}
		>
			<FormLayoutHeader
				text={l10nContext.translate('common.modal.checkDetails', 'Überprüfen Sie bitte Ihre Angaben')}
				icon={IconIdentifier.WARNING}
			/>

			{renderExtractionDatetimeInput()}

			<FormLayoutSection>
				<Label
					label={`${l10nContext.translate('eluates.activity', 'Aktivität')}*`}
				>
					<InputText
						type="text"
						defaultValue={l10nContext.formatNumber(eluate?.Activity) ?? ''}
						status={InputTextStatus.SUMMARY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection>
				<Label
					label={`${l10nContext.translate('nuclides.measurementUnits.unit', 'Einheit')}*`}
				>
					<InputText
						type='text'
						value={l10nContext.translate(getEluateMeasurementUnitDefinition(eluate?.ActivityUnit).getLabelKey())}
						status={InputTextStatus.SUMMARY}
						disabled
					/>
				</Label>
			</FormLayoutSection>

			<FormLayoutSection align={FormLayoutSectionAlign.ALIGN_BOTTOM}>
				<ColumnLayout>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FLEX}>
						<ButtonSecondary
							buttonText={l10nContext.translate('common.button.back', 'Zurück')}
							onClick={onClickPrevious}
						/>
					</ColumnLayoutSection>
					<ColumnLayoutSection mode={ColumnLayoutSectionMode.MODE_FIXED}>
						<ButtonPrimary
							buttonText={l10nContext.translate('view.eluates.modal.action.create.caption', 'Eluat-Entnahme erfassen')}
							type="submit"
						/>
					</ColumnLayoutSection>
				</ColumnLayout>
			</FormLayoutSection>
		</FormLayout>
	);
};
